import { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { useToast } from '@/components/ui/use-toast';
import { Loader2, FileCheck, AlertCircle, CreditCard, Settings, Building2, Plus, Pencil, Trash2, Star, X, Hash, Link2, RefreshCw, Database, CheckCircle2 } from 'lucide-react';

const REGIMEN_FISCAL_OPTIONS = [
  { code: '601', description: 'General de Ley Personas Morales' },
  { code: '603', description: 'Personas Morales con Fines no Lucrativos' },
  { code: '605', description: 'Sueldos y Salarios e Ingresos Asimilados a Salarios' },
  { code: '606', description: 'Arrendamiento' },
  { code: '607', description: 'Régimen de Enajenación o Adquisición de Bienes' },
  { code: '608', description: 'Demás ingresos' },
  { code: '610', description: 'Residentes en el Extranjero sin Establecimiento Permanente' },
  { code: '611', description: 'Ingresos por Dividendos (socios y accionistas)' },
  { code: '612', description: 'Personas Físicas con Actividades Empresariales y Profesionales' },
  { code: '614', description: 'Ingresos por intereses' },
  { code: '615', description: 'Régimen de los ingresos por obtención de premios' },
  { code: '616', description: 'Sin obligaciones fiscales' },
  { code: '620', description: 'Sociedades Cooperativas de Producción' },
  { code: '621', description: 'Incorporación Fiscal' },
  { code: '622', description: 'Actividades Agrícolas, Ganaderas, Silvícolas y Pesqueras' },
  { code: '623', description: 'Opcional para Grupos de Sociedades' },
  { code: '624', description: 'Coordinados' },
  { code: '625', description: 'Régimen de las Actividades Empresariales con Ingresos a través de Plataformas' },
  { code: '626', description: 'Régimen Simplificado de Confianza' },
];

interface InvoicingProfile {
  id?: number;
  profileName: string;
  isDefault: boolean;
  rfc: string;
  legalName: string;
  taxRegimeCode: string;
  zipCode: string;
  maxDaysToInvoice: number;
  generalInvoicePolicy: string;
  defaultUsoCfdi: string;
  defaultFormaPago: string;
  formasPago: string[];
  defaultMetodoPago: string;
  folioSeries: string;
  hasHubConfig: boolean;
  clientId?: string | null;
  hubUrl?: string | null;
  razonSocialId?: string | null;
  isConfigured: boolean;
}

interface StampBalance {
  stampsBalance: number;
  stampsUsed: number;
  expirationDate: string;
  isUnlimited: boolean;
}

interface EmisorStampBalance {
  id: number;
  profileName: string;
  rfc: string;
  legalName: string;
  isDefault: boolean;
  stampsBalance: number;
  stampsUsed: number;
  isUnlimited: boolean;
  alertaActiva: boolean;
  error: string | null;
}

interface PaymentConfig {
  formaPago: string;
  metodoPago: 'PUE' | 'PPD';
}

interface FolioSeries {
  id: number;
  invoicingSettingsId: number;
  serie: string;
  descripcion: string | null;
  currentFolio: number;
  isDefault: boolean;
  isActive: boolean;
  formaPago: string | null;
  metodoPago: string | null;
  maxDaysToInvoice: number | null;
  paymentConfigs: PaymentConfig[];
}

const FORMA_PAGO_OPTIONS = [
  { code: '01', label: '01 - Efectivo' },
  { code: '02', label: '02 - Cheque nominativo' },
  { code: '03', label: '03 - Transferencia electrónica' },
  { code: '04', label: '04 - Tarjeta de crédito' },
  { code: '28', label: '28 - A satisfacción del acreedor' },
  { code: '29', label: '29 - Tarjeta de débito' },
  { code: '30', label: '30 - Tarjeta de servicios' },
  { code: '99', label: '99 - Por definir' },
];

const emptyForm = (): Partial<InvoicingProfile> => ({
  profileName: '',
  isDefault: false,
  rfc: '',
  legalName: '',
  taxRegimeCode: '',
  zipCode: '',
  maxDaysToInvoice: 30,
  generalInvoicePolicy: '',
  defaultUsoCfdi: 'G03',
  defaultFormaPago: '03',
  formasPago: ['03'],
  defaultMetodoPago: 'PUE',
});

export default function BillingSettings() {
  const { toast } = useToast();
  const queryClient = useQueryClient();

  const [editingProfile, setEditingProfile] = useState<InvoicingProfile | null>(null);
  const [showForm, setShowForm] = useState(false);
  const [formData, setFormData] = useState<Partial<InvoicingProfile>>(emptyForm());

  // Hub config modal state
  const [hubProfileId, setHubProfileId] = useState<number | null>(null);
  const [hubForm, setHubForm] = useState({ hubUrl: '', clientId: '', clientSecret: '', changeSecret: false, razonSocialId: '', razonSocialRfc: '', razonSocialNombre: '' });
  const [testConnResult, setTestConnResult] = useState<{ razones?: Array<{ id: string; rfc: string; nombre: string }>; saldo?: { stampsBalance: number; isUnlimited: boolean }; error?: string } | null>(null);
  const [testConnLoading, setTestConnLoading] = useState(false);
  const [satSeedLoading, setSatSeedLoading] = useState(false);

  // Folio series management
  const [seriesProfileId, setSeriesProfileId] = useState<number | null>(null);
  const [seriesForm, setSeriesForm] = useState({
    serie: '',
    descripcion: '',
    isDefault: false,
    formaPago: '' as string,
    metodoPago: '' as string,
    maxDaysToInvoice: '' as string,
    paymentConfigs: [] as PaymentConfig[],
  });
  // Inline "add payment config" row state
  const [newPayConfig, setNewPayConfig] = useState({ formaPago: '', metodoPago: '' as string });
  const [editingSeries, setEditingSeries] = useState<FolioSeries | null>(null);

  const { data: profiles = [], isLoading: loadingProfiles } = useQuery<InvoicingProfile[]>({
    queryKey: ['cfdi-settings-all'],
    queryFn: async () => {
      const res = await fetch('/api/cfdi/settings/all', { credentials: 'include' });
      if (!res.ok) throw new Error('Error al cargar configuración');
      return res.json();
    },
  });

  const defaultProfile = profiles.find(p => p.isDefault) || profiles[0] || null;

  const { data: allBalances = [], isLoading: loadingBalance } = useQuery<EmisorStampBalance[]>({
    queryKey: ['cfdi-stamp-balance-all'],
    queryFn: async () => {
      const res = await fetch('/api/cfdi/stamp-balance/all', { credentials: 'include' });
      if (!res.ok) throw new Error('Error al consultar saldo');
      return res.json();
    },
    enabled: profiles.some(p => p.isConfigured),
    retry: false,
    staleTime: 60_000,
  });

  // Folio series for the selected profile
  const { data: folioSeries = [], isLoading: loadingSeries } = useQuery<FolioSeries[]>({
    queryKey: ['cfdi-folio-series', seriesProfileId],
    queryFn: async () => {
      if (!seriesProfileId) return [];
      const r = await fetch(`/api/cfdi/settings/${seriesProfileId}/series`, { credentials: 'include' });
      if (!r.ok) throw new Error('Error');
      return r.json();
    },
    enabled: !!seriesProfileId,
  });

  const resetSeriesForm = () => {
    setSeriesForm({ serie: '', descripcion: '', isDefault: false, formaPago: '', metodoPago: '', maxDaysToInvoice: '', paymentConfigs: [] });
    setNewPayConfig({ formaPago: '', metodoPago: '' });
    setEditingSeries(null);
  };

  const saveSeriesMutation = useMutation({
    mutationFn: async (data: { serie: string; descripcion: string; isDefault: boolean; formaPago: string; metodoPago: string; maxDaysToInvoice: string; paymentConfigs: PaymentConfig[]; id?: number }) => {
      if (!seriesProfileId) throw new Error('Sin perfil');
      const url = data.id
        ? `/api/cfdi/settings/${seriesProfileId}/series/${data.id}`
        : `/api/cfdi/settings/${seriesProfileId}/series`;
      const method = data.id ? 'PUT' : 'POST';
      const payload = {
        serie: data.serie,
        descripcion: data.descripcion,
        isDefault: data.isDefault,
        formaPago: data.formaPago || null,
        metodoPago: data.metodoPago || null,
        maxDaysToInvoice: data.maxDaysToInvoice ? parseInt(data.maxDaysToInvoice) : null,
        paymentConfigs: data.paymentConfigs,
      };
      const r = await fetch(url, {
        method,
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify(payload),
      });
      if (!r.ok) {
        const err = await r.json();
        throw new Error(err.error || 'Error al guardar');
      }
      return r.json();
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['cfdi-folio-series', seriesProfileId] });
      toast({ title: 'Serie guardada', description: 'La serie de folio fue guardada correctamente.' });
      resetSeriesForm();
    },
    onError: (err: Error) => toast({ title: 'Error', description: err.message, variant: 'destructive' }),
  });

  const deleteSeriesMutation = useMutation({
    mutationFn: async (seriesId: number) => {
      if (!seriesProfileId) throw new Error('Sin perfil');
      const r = await fetch(`/api/cfdi/settings/${seriesProfileId}/series/${seriesId}`, {
        method: 'DELETE',
        credentials: 'include',
      });
      if (!r.ok) {
        const err = await r.json();
        throw new Error(err.error || 'Error al eliminar');
      }
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['cfdi-folio-series', seriesProfileId] });
      toast({ title: 'Serie eliminada' });
    },
    onError: (err: Error) => toast({ title: 'Error', description: err.message, variant: 'destructive' }),
  });

  // Payment folio series management
  const [paymentSeriesProfileId, setPaymentSeriesProfileId] = useState<number | null>(null);
  const [paymentSeriesForm, setPaymentSeriesForm] = useState({ serie: '', descripcion: '' });
  const [editingPaymentSeries, setEditingPaymentSeries] = useState<{ id: number; serie: string; descripcion: string | null; isActive: boolean } | null>(null);

  const resetPaymentSeriesForm = () => {
    setPaymentSeriesForm({ serie: '', descripcion: '' });
    setEditingPaymentSeries(null);
  };

  const { data: paymentFolioSeriesList = [], isLoading: loadingPaymentSeries } = useQuery({
    queryKey: ['payment-folio-series', paymentSeriesProfileId],
    queryFn: async () => {
      const res = await fetch(`/api/payment-folio-series?invoicingSettingsId=${paymentSeriesProfileId}`, { credentials: 'include' });
      if (!res.ok) throw new Error('Error al cargar series de pago');
      return res.json();
    },
    enabled: !!paymentSeriesProfileId,
  });

  const { data: paymentDefaults = [] } = useQuery({
    queryKey: ['payment-folio-series-defaults', paymentSeriesProfileId],
    queryFn: async () => {
      const res = await fetch(`/api/payment-folio-series/defaults?invoicingSettingsId=${paymentSeriesProfileId}`, { credentials: 'include' });
      if (!res.ok) throw new Error('Error al cargar mapeo default');
      return res.json() as Promise<Array<{ paymentMethod: string; seriesId: number; seriesName: string }>>;
    },
    enabled: !!paymentSeriesProfileId,
  });

  const savePaymentSeriesMutation = useMutation({
    mutationFn: async () => {
      const method = editingPaymentSeries ? 'PUT' : 'POST';
      const url = editingPaymentSeries ? `/api/payment-folio-series/${editingPaymentSeries.id}` : '/api/payment-folio-series';
      const body = editingPaymentSeries
        ? { descripcion: paymentSeriesForm.descripcion }
        : { invoicingSettingsId: paymentSeriesProfileId, serie: paymentSeriesForm.serie, descripcion: paymentSeriesForm.descripcion };
      const res = await fetch(url, { method, headers: { 'Content-Type': 'application/json' }, credentials: 'include', body: JSON.stringify(body) });
      if (!res.ok) { const err = await res.json(); throw new Error(err.error); }
      return res.json();
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['payment-folio-series', paymentSeriesProfileId] });
      toast({ title: 'Serie de pago guardada' });
      resetPaymentSeriesForm();
    },
    onError: (e: Error) => toast({ title: 'Error', description: e.message, variant: 'destructive' }),
  });

  const deactivatePaymentSeriesMutation = useMutation({
    mutationFn: async (id: number) => {
      const res = await fetch(`/api/payment-folio-series/${id}`, {
        method: 'PUT', headers: { 'Content-Type': 'application/json' }, credentials: 'include', body: JSON.stringify({ isActive: false }),
      });
      if (!res.ok) { const err = await res.json(); throw new Error(err.error); }
      return res.json();
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['payment-folio-series', paymentSeriesProfileId] });
      toast({ title: 'Serie desactivada' });
    },
    onError: (e: Error) => toast({ title: 'Error', description: e.message, variant: 'destructive' }),
  });

  const setDefaultMutation = useMutation({
    mutationFn: async ({ paymentMethod, seriesId }: { paymentMethod: string; seriesId: number }) => {
      const res = await fetch('/api/payment-folio-series/defaults', {
        method: 'PUT', headers: { 'Content-Type': 'application/json' }, credentials: 'include',
        body: JSON.stringify({ invoicingSettingsId: paymentSeriesProfileId, paymentMethod, seriesId }),
      });
      if (!res.ok) { const err = await res.json(); throw new Error(err.error); }
      return res.json();
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['payment-folio-series-defaults', paymentSeriesProfileId] });
      toast({ title: 'Serie default asignada' });
    },
    onError: (e: Error) => toast({ title: 'Error', description: e.message, variant: 'destructive' }),
  });

  const PAYMENT_METHODS = ['CASH', 'TRANSFER', 'CARD', 'CHECK', 'OTHER', 'CREDIT'] as const;
  const PAYMENT_METHOD_LABELS: Record<string, string> = { CASH: 'Efectivo', TRANSFER: 'Transferencia', CARD: 'Tarjeta', CHECK: 'Cheque', OTHER: 'Otro', CREDIT: 'Crédito' };

  const saveProfileMutation = useMutation({
    mutationFn: async (data: { formData: Partial<InvoicingProfile>; id?: number }) => {
      const url = data.id ? `/api/cfdi/settings/${data.id}` : '/api/cfdi/settings';
      const method = data.id ? 'PUT' : 'POST';
      const res = await fetch(url, {
        method,
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify(data.formData),
      });
      if (!res.ok) {
        const err = await res.json();
        throw new Error(err.error || 'Error al guardar');
      }
      return res.json();
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['cfdi-settings-all'] });
      queryClient.invalidateQueries({ queryKey: ['cfdi-settings'] });
      toast({ title: 'Guardado', description: 'Los datos fiscales se actualizaron correctamente.' });
      setShowForm(false);
      setEditingProfile(null);
      setFormData(emptyForm());
    },
    onError: (error: Error) => {
      toast({ title: 'Error', description: error.message, variant: 'destructive' });
    },
  });

  const deleteProfileMutation = useMutation({
    mutationFn: async (id: number) => {
      const res = await fetch(`/api/cfdi/settings/${id}`, {
        method: 'DELETE',
        credentials: 'include',
      });
      if (!res.ok) {
        const err = await res.json();
        throw new Error(err.error || 'Error al eliminar');
      }
      return res.json();
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['cfdi-settings-all'] });
      queryClient.invalidateQueries({ queryKey: ['cfdi-settings'] });
      toast({ title: 'Eliminado', description: 'El perfil fiscal fue eliminado.' });
    },
    onError: (error: Error) => {
      toast({ title: 'Error', description: error.message, variant: 'destructive' });
    },
  });

  const saveHubConfigMutation = useMutation({
    mutationFn: async (data: { settingsId: number; hubUrl?: string; clientId: string; clientSecret?: string; razonSocialId: string; razonSocialRfc?: string; razonSocialNombre?: string }) => {
      const { settingsId, ...body } = data;
      const res = await fetch(`/api/cfdi/settings/${settingsId}/hub-config`, {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify(body),
      });
      if (!res.ok) {
        const err = await res.json();
        throw new Error(err.error || 'Error al guardar configuración Hub');
      }
      return res.json();
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['cfdi-settings-all'] });
      queryClient.invalidateQueries({ queryKey: ['cfdi-stamp-balance'] });
      queryClient.invalidateQueries({ queryKey: ['cfdi-stamp-balance-all'] });
      toast({ title: 'Configuración Hub guardada', description: 'Las credenciales del hub de timbrado fueron actualizadas.' });
      setHubProfileId(null);
      setTestConnResult(null);
    },
    onError: (error: Error) => {
      toast({ title: 'Error', description: error.message, variant: 'destructive' });
    },
  });

  const handleOpenCreate = () => {
    setEditingProfile(null);
    setFormData(emptyForm());
    setShowForm(true);
  };

  const handleOpenEdit = (profile: InvoicingProfile) => {
    setEditingProfile(profile);
    // Normalize formasPago: ensure it's an array and at least has defaultFormaPago
    const rawFormasPago = Array.isArray(profile.formasPago) && profile.formasPago.length > 0
      ? profile.formasPago
      : [profile.defaultFormaPago || '03'];
    setFormData({
      profileName: profile.profileName || '',
      isDefault: profile.isDefault,
      rfc: profile.rfc || '',
      legalName: profile.legalName || '',
      taxRegimeCode: profile.taxRegimeCode || '',
      zipCode: profile.zipCode || '',
      maxDaysToInvoice: profile.maxDaysToInvoice || 30,
      generalInvoicePolicy: profile.generalInvoicePolicy || '',
      defaultUsoCfdi: profile.defaultUsoCfdi || 'G03',
      defaultFormaPago: rawFormasPago.includes(profile.defaultFormaPago || '') ? (profile.defaultFormaPago || '03') : rawFormasPago[0],
      formasPago: rawFormasPago,
      defaultMetodoPago: profile.defaultMetodoPago || 'PUE',
    });
    setShowForm(true);
  };

  const handleSave = () => {
    if (!formData.rfc || !formData.legalName || !formData.taxRegimeCode || !formData.zipCode || !formData.profileName) {
      toast({ title: 'Error', description: 'Complete todos los campos requeridos (incluido el nombre del perfil)', variant: 'destructive' });
      return;
    }
    saveProfileMutation.mutate({ formData, id: editingProfile?.id });
  };

  const handleDelete = (profile: InvoicingProfile) => {
    if (!profile.id) return;
    if (!confirm(`¿Está seguro de eliminar el perfil "${profile.profileName}"?`)) return;
    deleteProfileMutation.mutate(profile.id);
  };

  const handleOpenHubConfig = (profile: InvoicingProfile) => {
    setHubProfileId(profile.id!);
    setHubForm({
      hubUrl: profile.hubUrl || '',
      clientId: profile.clientId || '',
      clientSecret: '',
      changeSecret: !profile.hasHubConfig,
      razonSocialId: profile.razonSocialId || '',
      razonSocialRfc: '',
      razonSocialNombre: '',
    });
    setTestConnResult(null);
  };

  const handleTestConnection = async () => {
    if (!hubProfileId) return;
    setTestConnLoading(true);
    setTestConnResult(null);
    try {
      const res = await fetch(`/api/cfdi/settings/test-connection?settingsId=${hubProfileId}`, { credentials: 'include' });
      if (!res.ok) {
        const err = await res.json();
        setTestConnResult({ error: err.error || 'Error de conexión' });
      } else {
        const data = await res.json();
        setTestConnResult(data);
      }
    } catch (e: any) {
      setTestConnResult({ error: e.message });
    } finally {
      setTestConnLoading(false);
    }
  };

  const handleSaveHubConfig = () => {
    if (!hubProfileId) return;
    if (!hubForm.clientId || !hubForm.razonSocialId) {
      toast({ title: 'Error', description: 'Client ID y Razón Social ID son requeridos', variant: 'destructive' });
      return;
    }
    if (hubForm.changeSecret && !hubForm.clientSecret) {
      toast({ title: 'Error', description: 'Ingrese el Client Secret', variant: 'destructive' });
      return;
    }
    saveHubConfigMutation.mutate({
      settingsId: hubProfileId,
      hubUrl: hubForm.hubUrl || undefined,
      clientId: hubForm.clientId,
      ...(hubForm.changeSecret ? { clientSecret: hubForm.clientSecret } : {}),
      razonSocialId: hubForm.razonSocialId,
      razonSocialRfc: hubForm.razonSocialRfc || undefined,
      razonSocialNombre: hubForm.razonSocialNombre || undefined,
    });
  };

  const handleSatSeed = async () => {
    setSatSeedLoading(true);
    try {
      const res = await fetch('/api/cfdi/sat/seed', { method: 'POST', credentials: 'include' });
      if (!res.ok) {
        const err = await res.json();
        throw new Error(err.error || 'Error al cargar catálogos');
      }
      const data = await res.json();
      toast({ title: 'Catálogos cargados', description: `Se cargaron ${data.unidades} unidades y ${data.prodServ} claves de producto/servicio.` });
    } catch (e: any) {
      toast({ title: 'Error', description: e.message, variant: 'destructive' });
    } finally {
      setSatSeedLoading(false);
    }
  };

  const getRegimenLabel = (code: string) => {
    const r = REGIMEN_FISCAL_OPTIONS.find(o => o.code === code);
    return r ? `${r.code} - ${r.description}` : code;
  };

  if (loadingProfiles) {
    return (
      <div className="flex items-center justify-center p-8">
        <Loader2 className="h-8 w-8 animate-spin" />
      </div>
    );
  }

  return (
    <div className="container mx-auto p-6 max-w-4xl space-y-6">
      <div className="flex items-center gap-3 mb-6">
        <Settings className="h-8 w-8 text-primary" />
        <div>
          <h1 className="text-2xl font-bold">Configuración de Facturación Electrónica</h1>
          <p className="text-muted-foreground">Configure sus datos fiscales y certificados para emitir CFDI 4.0</p>
        </div>
      </div>

      <div className="grid gap-6 md:grid-cols-2">
        <Card className={defaultProfile?.isConfigured ? 'border-green-500/50' : 'border-yellow-500/50'}>
          <CardHeader className="pb-3">
            <div className="flex items-center gap-2">
              {defaultProfile?.isConfigured ? (
                <FileCheck className="h-5 w-5 text-green-500" />
              ) : (
                <AlertCircle className="h-5 w-5 text-yellow-500" />
              )}
              <CardTitle className="text-lg">Estado de Configuración</CardTitle>
            </div>
          </CardHeader>
          <CardContent>
            <div className="space-y-2 text-sm">
              <div className="flex justify-between">
                <span>Perfiles fiscales:</span>
                <span className={profiles.length > 0 ? 'text-green-600' : 'text-yellow-600'}>
                  {profiles.length > 0 ? `${profiles.length} registrado(s)` : 'Pendiente'}
                </span>
              </div>
              <div className="flex justify-between">
                <span>Perfil principal:</span>
                <span className={defaultProfile?.rfc ? 'text-green-600' : 'text-yellow-600'}>
                  {defaultProfile?.rfc ? defaultProfile.rfc : 'Pendiente'}
                </span>
              </div>
              <div className="flex justify-between">
                <span>Hub de timbrado:</span>
                <span className={defaultProfile?.hasHubConfig ? 'text-green-600' : 'text-yellow-600'}>
                  {defaultProfile?.hasHubConfig ? 'Configurado' : 'Pendiente'}
                </span>
              </div>
              {defaultProfile?.razonSocialId && (
                <div className="flex justify-between">
                  <span>Razón Social Hub:</span>
                  <span className="text-green-600 text-xs truncate max-w-[160px]">{defaultProfile.razonSocialId}</span>
                </div>
              )}
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="pb-3">
            <div className="flex items-center gap-2">
              <CreditCard className="h-5 w-5 text-primary" />
              <CardTitle className="text-lg">Saldo de Timbres</CardTitle>
            </div>
          </CardHeader>
          <CardContent>
            {loadingBalance ? (
              <div className="flex items-center gap-2 text-sm text-muted-foreground">
                <Loader2 className="h-4 w-4 animate-spin" />
                Consultando saldo…
              </div>
            ) : allBalances.length === 0 ? (
              <p className="text-sm text-muted-foreground">
                Configure las credenciales Hub para consultar el saldo
              </p>
            ) : (
              <div className="space-y-3">
                {allBalances.map(b => (
                  <div key={b.id} className="flex items-start justify-between rounded-lg border p-3 gap-3">
                    <div className="min-w-0">
                      <p className="text-sm font-semibold truncate">
                        {b.profileName || b.legalName}
                        {b.isDefault && (
                          <span className="ml-2 text-xs text-muted-foreground font-normal">(predeterminado)</span>
                        )}
                      </p>
                      <p className="text-xs text-muted-foreground font-mono">{b.rfc}</p>
                    </div>
                    <div className="text-right shrink-0">
                      {b.error ? (
                        <p className="text-xs text-destructive">{b.error}</p>
                      ) : (
                        <>
                          <p className={`text-2xl font-bold ${b.alertaActiva ? 'text-destructive' : 'text-primary'}`}>
                            {b.isUnlimited ? '∞' : b.stampsBalance.toLocaleString()}
                          </p>
                          <p className="text-xs text-muted-foreground">disponibles</p>
                          {!b.isUnlimited && b.stampsUsed > 0 && (
                            <p className="text-xs text-muted-foreground">
                              {b.stampsUsed.toLocaleString()} usados
                            </p>
                          )}
                          {b.alertaActiva && (
                            <p className="text-xs text-destructive font-medium mt-0.5">⚠ Saldo bajo</p>
                          )}
                        </>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            )}
          </CardContent>
        </Card>
      </div>

      {/* Issuer profiles list */}
      <Card>
        <CardHeader>
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2">
              <Building2 className="h-5 w-5" />
              <div>
                <CardTitle>Datos Fiscales del Emisor</CardTitle>
                <CardDescription>Perfiles fiscales de la institución para emisión de facturas</CardDescription>
              </div>
            </div>
            <Button onClick={handleOpenCreate} size="sm">
              <Plus className="h-4 w-4 mr-1" />
              Nuevo Perfil
            </Button>
          </div>
        </CardHeader>
        <CardContent>
          {profiles.length === 0 && !showForm ? (
            <div className="text-center py-8 text-muted-foreground">
              <Building2 className="h-12 w-12 mx-auto mb-4 opacity-50" />
              <p>No hay perfiles fiscales registrados.</p>
              <p className="text-sm">Haga clic en "Nuevo Perfil" para crear uno.</p>
            </div>
          ) : (
            <div className="space-y-3">
              {profiles.map((profile) => (
                <div
                  key={profile.id}
                  className="flex items-center justify-between p-4 border rounded-lg hover:bg-gray-50 transition-colors"
                >
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 mb-1">
                      <span className="font-semibold text-sm">{profile.profileName}</span>
                      {profile.isDefault && (
                        <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs bg-yellow-100 text-yellow-800">
                          <Star className="h-3 w-3" />
                          Principal
                        </span>
                      )}
                      {profile.hasHubConfig && (
                        <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs bg-green-100 text-green-800">
                          <Link2 className="h-3 w-3" />
                          Hub
                        </span>
                      )}
                    </div>
                    <div className="text-sm text-gray-600">
                      <span className="font-medium">{profile.rfc}</span> — {profile.legalName}
                    </div>
                    <div className="text-xs text-gray-400 mt-0.5">
                      Régimen: {getRegimenLabel(profile.taxRegimeCode)} | C.P. {profile.zipCode}
                    </div>
                    {(Array.isArray(profile.formasPago) && profile.formasPago.length > 0) ? (
                      <div className="flex flex-wrap gap-1 mt-1">
                        {profile.formasPago.map(code => {
                          const opt = FORMA_PAGO_OPTIONS.find(o => o.code === code);
                          const isDefault = code === profile.defaultFormaPago;
                          return (
                            <span
                              key={code}
                              className={`px-1.5 py-0.5 rounded text-xs font-medium ${
                                isDefault
                                  ? 'bg-blue-100 text-blue-700'
                                  : 'bg-gray-100 text-gray-500'
                              }`}
                            >
                              {isDefault && '★ '}{opt ? opt.label : code}
                            </span>
                          );
                        })}
                      </div>
                    ) : (
                      <div className="text-xs text-gray-400 mt-0.5">
                        Forma de pago: {FORMA_PAGO_OPTIONS.find(o => o.code === profile.defaultFormaPago)?.label || profile.defaultFormaPago}
                      </div>
                    )}
                  </div>
                  <div className="flex items-center gap-2 ml-4">
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => { setSeriesProfileId(profile.id!); resetSeriesForm(); }}
                      title="Gestionar series de folio"
                    >
                      <Hash className="h-4 w-4" />
                    </Button>
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => { setPaymentSeriesProfileId(profile.id!); resetPaymentSeriesForm(); }}
                      title="Gestionar series de pago"
                    >
                      Series de Pago
                    </Button>
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => handleOpenHubConfig(profile)}
                      title="Configurar Hub QFacturaHub"
                    >
                      <Link2 className="h-4 w-4" />
                    </Button>
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => handleOpenEdit(profile)}
                      title="Editar"
                    >
                      <Pencil className="h-4 w-4" />
                    </Button>
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => handleDelete(profile)}
                      className="text-red-600 hover:text-red-700 hover:bg-red-50"
                      title="Eliminar"
                    >
                      <Trash2 className="h-4 w-4" />
                    </Button>
                  </div>
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>

      {/* Create/Edit Form Modal */}
      {showForm && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg shadow-xl w-full max-w-2xl max-h-[90vh] overflow-y-auto">
            <div className="flex justify-between items-center px-6 py-4 border-b sticky top-0 bg-white z-10">
              <h2 className="text-xl font-semibold text-gray-900 flex items-center gap-2">
                <Building2 className="h-5 w-5" />
                {editingProfile ? 'Editar Perfil Fiscal' : 'Nuevo Perfil Fiscal'}
              </h2>
              <button
                onClick={() => { setShowForm(false); setEditingProfile(null); setFormData(emptyForm()); }}
                className="text-gray-400 hover:text-gray-600"
              >
                <X className="h-5 w-5" />
              </button>
            </div>

            <div className="p-6 space-y-4">
              <div className="grid gap-4 md:grid-cols-2">
                <div className="space-y-2">
                  <Label htmlFor="profileName">Nombre del Perfil *</Label>
                  <Input
                    id="profileName"
                    value={formData.profileName}
                    onChange={(e) => setFormData({ ...formData, profileName: e.target.value })}
                    placeholder="Ej: Empresa Principal, Sucursal Norte"
                  />
                </div>
                <div className="space-y-2 flex items-end">
                  <label className="flex items-center gap-2 cursor-pointer">
                    <input
                      type="checkbox"
                      checked={formData.isDefault || false}
                      onChange={(e) => setFormData({ ...formData, isDefault: e.target.checked })}
                      className="rounded border-gray-300"
                    />
                    <span className="text-sm font-medium">Marcar como perfil principal</span>
                  </label>
                </div>
              </div>

              <div className="grid gap-4 md:grid-cols-2">
                <div className="space-y-2">
                  <Label htmlFor="rfc">RFC *</Label>
                  <Input
                    id="rfc"
                    value={formData.rfc}
                    onChange={(e) => setFormData({ ...formData, rfc: e.target.value.toUpperCase() })}
                    placeholder="XAXX010101000"
                    maxLength={13}
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="zipCode">Código Postal *</Label>
                  <Input
                    id="zipCode"
                    value={formData.zipCode}
                    onChange={(e) => setFormData({ ...formData, zipCode: e.target.value })}
                    placeholder="00000"
                    maxLength={5}
                  />
                </div>
              </div>

              <div className="space-y-2">
                <Label htmlFor="legalName">Razón Social *</Label>
                <Input
                  id="legalName"
                  value={formData.legalName}
                  onChange={(e) => setFormData({ ...formData, legalName: e.target.value })}
                  placeholder="NOMBRE O RAZÓN SOCIAL"
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="taxRegime">Régimen Fiscal *</Label>
                <Select
                  value={formData.taxRegimeCode}
                  onValueChange={(value) => setFormData({ ...formData, taxRegimeCode: value })}
                >
                  <SelectTrigger>
                    <SelectValue placeholder="Seleccione régimen fiscal" />
                  </SelectTrigger>
                  <SelectContent>
                    {REGIMEN_FISCAL_OPTIONS.map((regime) => (
                      <SelectItem key={regime.code} value={regime.code}>
                        {regime.code} - {regime.description}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              <div className="space-y-3">
                <div>
                  <Label>Formas de Pago aceptadas</Label>
                  <p className="text-xs text-muted-foreground mt-0.5 mb-2">Selecciona todas las formas de pago que acepta esta razón social</p>
                  <div className="grid grid-cols-2 gap-2">
                    {FORMA_PAGO_OPTIONS.map((opt) => {
                      const isChecked = (formData.formasPago || []).includes(opt.code);
                      return (
                        <label
                          key={opt.code}
                          className={`flex items-center gap-2 p-2 rounded-lg border cursor-pointer transition-colors ${
                            isChecked ? 'border-primary bg-primary/5' : 'border-border hover:bg-accent/50'
                          }`}
                        >
                          <input
                            type="checkbox"
                            checked={isChecked}
                            onChange={(e) => {
                              const current = formData.formasPago || [];
                              let next: string[];
                              if (e.target.checked) {
                                next = [...current, opt.code];
                              } else {
                                // Must keep at least one selected
                                if (current.length <= 1) return;
                                next = current.filter(c => c !== opt.code);
                              }
                              // If the defaultFormaPago was removed, switch to first remaining
                              const newDefault = next.includes(formData.defaultFormaPago || '')
                                ? formData.defaultFormaPago
                                : next[0];
                              setFormData({ ...formData, formasPago: next, defaultFormaPago: newDefault });
                            }}
                            className="w-4 h-4 rounded border-gray-300 text-primary"
                          />
                          <span className="text-sm">{opt.label}</span>
                        </label>
                      );
                    })}
                  </div>
                </div>

                {(formData.formasPago || []).length > 1 && (
                  <div className="space-y-2">
                    <Label>Forma de Pago predeterminada</Label>
                    <Select
                      value={formData.defaultFormaPago}
                      onValueChange={(value) => setFormData({ ...formData, defaultFormaPago: value })}
                    >
                      <SelectTrigger>
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        {(formData.formasPago || []).map(code => {
                          const opt = FORMA_PAGO_OPTIONS.find(o => o.code === code);
                          return (
                            <SelectItem key={code} value={code}>
                              {opt ? opt.label : code}
                            </SelectItem>
                          );
                        })}
                      </SelectContent>
                    </Select>
                    <p className="text-xs text-muted-foreground">Se usará esta forma de pago al generar CFDIs automáticamente</p>
                  </div>
                )}
              </div>

              <div className="grid gap-4 md:grid-cols-2">
                <div className="space-y-2">
                  <Label htmlFor="defaultMetodoPago">Método de Pago</Label>
                  <Select
                    value={formData.defaultMetodoPago}
                    onValueChange={(value) => setFormData({ ...formData, defaultMetodoPago: value })}
                  >
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="PUE">PUE - Pago en una sola exhibición</SelectItem>
                      <SelectItem value="PPD">PPD - Pago en parcialidades o diferido</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>

              <div className="space-y-2 max-w-xs">
                <Label htmlFor="maxDays">Días máximos para facturar</Label>
                <Input
                  id="maxDays"
                  type="number"
                  min={1}
                  max={365}
                  value={formData.maxDaysToInvoice}
                  onChange={(e) => setFormData({ ...formData, maxDaysToInvoice: parseInt(e.target.value) || 30 })}
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="policy">Política de Facturación</Label>
                <Textarea
                  id="policy"
                  value={formData.generalInvoicePolicy}
                  onChange={(e) => setFormData({ ...formData, generalInvoicePolicy: e.target.value })}
                  placeholder="Escriba aquí las políticas de facturación..."
                  rows={3}
                />
              </div>

              <div className="flex justify-end space-x-3 pt-4 border-t">
                <Button
                  variant="outline"
                  onClick={() => { setShowForm(false); setEditingProfile(null); setFormData(emptyForm()); }}
                >
                  Cancelar
                </Button>
                <Button
                  onClick={handleSave}
                  disabled={saveProfileMutation.isPending}
                >
                  {saveProfileMutation.isPending && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
                  {editingProfile ? 'Actualizar' : 'Crear'} Perfil
                </Button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Folio Series Modal */}
      {seriesProfileId && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg shadow-xl w-full max-w-xl max-h-[90vh] overflow-y-auto">
            <div className="flex justify-between items-center px-6 py-4 border-b sticky top-0 bg-white z-10">
              <h2 className="text-lg font-semibold text-gray-900 flex items-center gap-2">
                <Hash className="h-5 w-5" />
                Series de Folio — {profiles.find(p => p.id === seriesProfileId)?.rfc}
              </h2>
              <button
                onClick={() => { setSeriesProfileId(null); resetSeriesForm(); }}
                className="text-gray-400 hover:text-gray-600"
              >
                <X className="h-5 w-5" />
              </button>
            </div>

            <div className="p-6 space-y-5">
              {/* Existing series list */}
              {loadingSeries ? (
                <div className="flex justify-center py-4"><Loader2 className="h-5 w-5 animate-spin text-gray-400" /></div>
              ) : folioSeries.length === 0 ? (
                <p className="text-sm text-gray-400 text-center py-4">No hay series configuradas. Agrega la primera abajo.</p>
              ) : (
                <div className="space-y-2">
                  {folioSeries.map((s) => (
                    <div key={s.id} className="flex items-center gap-3 p-3 border rounded-lg hover:bg-gray-50">
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2">
                          <span className="font-bold text-sm font-mono bg-gray-100 px-2 py-0.5 rounded">{s.serie}</span>
                          {s.isDefault && (
                            <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs bg-yellow-100 text-yellow-800">
                              <Star className="h-3 w-3" /> Principal
                            </span>
                          )}
                        </div>
                        {s.descripcion && <p className="text-xs text-gray-500 mt-0.5">{s.descripcion}</p>}
                        <div className="flex flex-wrap gap-2 mt-1">
                          <p className="text-xs text-gray-400">Folio actual: <strong>{s.currentFolio}</strong></p>
                          {s.maxDaysToInvoice != null && (
                            <span className="text-xs text-gray-500 bg-gray-100 px-1.5 py-0.5 rounded">{s.maxDaysToInvoice}d</span>
                          )}
                          {Array.isArray(s.paymentConfigs) && s.paymentConfigs.length > 0 && s.paymentConfigs.map((pc, idx) => {
                            const fpLabel = FORMA_PAGO_OPTIONS.find(o => o.code === pc.formaPago)?.label || pc.formaPago;
                            return (
                              <span key={idx} className="text-xs text-indigo-700 bg-indigo-50 border border-indigo-200 px-1.5 py-0.5 rounded">
                                {fpLabel} · {pc.metodoPago}
                              </span>
                            );
                          })}
                        </div>
                      </div>
                      <div className="flex items-center gap-1.5 shrink-0">
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => {
                            setEditingSeries(s);
                            setNewPayConfig({ formaPago: '', metodoPago: '' });
                            setSeriesForm({
                              serie: s.serie,
                              descripcion: s.descripcion || '',
                              isDefault: s.isDefault,
                              formaPago: s.formaPago || '',
                              metodoPago: s.metodoPago || '',
                              maxDaysToInvoice: s.maxDaysToInvoice != null ? String(s.maxDaysToInvoice) : '',
                              paymentConfigs: Array.isArray(s.paymentConfigs) ? s.paymentConfigs : [],
                            });
                          }}
                        >
                          <Pencil className="h-3.5 w-3.5" />
                        </Button>
                        <Button
                          variant="outline"
                          size="sm"
                          className="text-red-600 hover:text-red-700 hover:bg-red-50"
                          onClick={() => {
                            if (!confirm(`¿Eliminar la serie "${s.serie}"?`)) return;
                            deleteSeriesMutation.mutate(s.id);
                          }}
                          disabled={deleteSeriesMutation.isPending}
                        >
                          <Trash2 className="h-3.5 w-3.5" />
                        </Button>
                      </div>
                    </div>
                  ))}
                </div>
              )}

              {/* Add / Edit form */}
              <div className="border-t pt-4 space-y-3">
                <h3 className="text-sm font-semibold text-gray-700">
                  {editingSeries ? `Editar serie "${editingSeries.serie}"` : 'Agregar nueva serie'}
                </h3>
                <div className="grid grid-cols-2 gap-3">
                  <div className="space-y-1">
                    <Label className="text-xs">Serie *</Label>
                    <Input
                      value={seriesForm.serie}
                      onChange={(e) => setSeriesForm({ ...seriesForm, serie: e.target.value.toUpperCase() })}
                      placeholder="Ej: A, B, FAC"
                      maxLength={10}
                      className="font-mono"
                    />
                  </div>
                  <div className="space-y-1">
                    <Label className="text-xs">Descripción (opcional)</Label>
                    <Input
                      value={seriesForm.descripcion}
                      onChange={(e) => setSeriesForm({ ...seriesForm, descripcion: e.target.value })}
                      placeholder="Ej: Serie general, CFDI exportación"
                    />
                  </div>
                </div>

                <div className="space-y-2">
                  <div className="flex items-center justify-between">
                    <p className="text-xs text-gray-500 font-medium">Métodos de pago aceptados <span className="text-gray-400">(sobreescriben el perfil cuando se especifican)</span></p>
                  </div>

                  {/* Current payment configs list */}
                  {seriesForm.paymentConfigs.length > 0 && (
                    <div className="space-y-1">
                      {seriesForm.paymentConfigs.map((pc, idx) => {
                        const fpLabel = FORMA_PAGO_OPTIONS.find(o => o.code === pc.formaPago)?.label || pc.formaPago;
                        return (
                          <div key={idx} className="flex items-center justify-between bg-indigo-50 border border-indigo-200 rounded px-2 py-1">
                            <span className="text-xs text-indigo-800">
                              <span className="font-medium">{fpLabel}</span>
                              <span className="mx-1 text-indigo-400">·</span>
                              <span className="font-mono font-semibold">{pc.metodoPago}</span>
                            </span>
                            <button
                              type="button"
                              onClick={() => setSeriesForm(f => ({ ...f, paymentConfigs: f.paymentConfigs.filter((_, i) => i !== idx) }))}
                              className="text-indigo-400 hover:text-red-500 ml-2"
                            >
                              <X className="h-3.5 w-3.5" />
                            </button>
                          </div>
                        );
                      })}
                    </div>
                  )}

                  {/* Add new combo row */}
                  <div className="flex items-center gap-2">
                    <Select
                      value={newPayConfig.formaPago || '__none__'}
                      onValueChange={(v) => setNewPayConfig(p => ({ ...p, formaPago: v === '__none__' ? '' : v }))}
                    >
                      <SelectTrigger className="text-xs h-8 flex-1">
                        <SelectValue placeholder="Forma de pago" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="__none__">— Forma de pago —</SelectItem>
                        {FORMA_PAGO_OPTIONS.map(fp => (
                          <SelectItem key={fp.code} value={fp.code}>{fp.label}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                    <Select
                      value={newPayConfig.metodoPago || '__none__'}
                      onValueChange={(v) => setNewPayConfig(p => ({ ...p, metodoPago: v === '__none__' ? '' : v }))}
                    >
                      <SelectTrigger className="text-xs h-8 w-40">
                        <SelectValue placeholder="Método" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="__none__">— Método —</SelectItem>
                        <SelectItem value="PUE">PUE - Una exhibición</SelectItem>
                        <SelectItem value="PPD">PPD - Parcialidades</SelectItem>
                      </SelectContent>
                    </Select>
                    <Button
                      type="button"
                      size="sm"
                      variant="outline"
                      className="h-8 text-xs shrink-0"
                      onClick={() => {
                        if (!newPayConfig.formaPago || !newPayConfig.metodoPago) return;
                        setSeriesForm(f => ({
                          ...f,
                          paymentConfigs: [...f.paymentConfigs, { formaPago: newPayConfig.formaPago, metodoPago: newPayConfig.metodoPago as 'PUE' | 'PPD' }],
                        }));
                        setNewPayConfig({ formaPago: '', metodoPago: '' });
                      }}
                      disabled={!newPayConfig.formaPago || !newPayConfig.metodoPago}
                    >
                      <Plus className="h-3.5 w-3.5 mr-1" />
                      Agregar
                    </Button>
                  </div>
                </div>

                <div className="space-y-1">
                  <Label className="text-xs">Días máx. para facturar <span className="text-gray-400">(opcional)</span></Label>
                  <Input
                    type="number"
                    min={1}
                    max={365}
                    value={seriesForm.maxDaysToInvoice}
                    onChange={(e) => setSeriesForm({ ...seriesForm, maxDaysToInvoice: e.target.value })}
                    placeholder="Usar del perfil"
                    className="h-8 text-xs w-48"
                  />
                </div>

                <label className="flex items-center gap-2 cursor-pointer text-sm">
                  <input
                    type="checkbox"
                    checked={seriesForm.isDefault}
                    onChange={(e) => setSeriesForm({ ...seriesForm, isDefault: e.target.checked })}
                    className="rounded border-gray-300"
                  />
                  Marcar como serie predeterminada
                </label>
                <div className="flex gap-2 justify-end">
                  {editingSeries && (
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => resetSeriesForm()}
                    >
                      Cancelar edición
                    </Button>
                  )}
                  <Button
                    size="sm"
                    onClick={() => {
                      if (!seriesForm.serie.trim()) {
                        toast({ title: 'La serie es requerida', variant: 'destructive' });
                        return;
                      }
                      saveSeriesMutation.mutate({
                        serie: seriesForm.serie.trim(),
                        descripcion: seriesForm.descripcion.trim(),
                        isDefault: seriesForm.isDefault,
                        formaPago: seriesForm.formaPago,
                        metodoPago: seriesForm.metodoPago,
                        maxDaysToInvoice: seriesForm.maxDaysToInvoice,
                        paymentConfigs: seriesForm.paymentConfigs,
                        id: editingSeries?.id,
                      });
                    }}
                    disabled={saveSeriesMutation.isPending}
                  >
                    {saveSeriesMutation.isPending && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}
                    {editingSeries ? 'Actualizar' : 'Agregar'} Serie
                  </Button>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Payment Folio Series Modal */}
      {paymentSeriesProfileId && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg shadow-xl w-full max-w-2xl max-h-[85vh] overflow-y-auto">
            <div className="flex items-center justify-between p-5 border-b">
              <h2 className="text-lg font-semibold">
                Series de Pago — {profiles.find((p) => p.id === paymentSeriesProfileId)?.rfc}
              </h2>
              <button onClick={() => { setPaymentSeriesProfileId(null); resetPaymentSeriesForm(); }} className="text-gray-400 hover:text-gray-600">
                <span className="text-xl leading-none">&times;</span>
              </button>
            </div>

            <div className="p-5 space-y-5">
              {loadingPaymentSeries ? (
                <p className="text-sm text-gray-500">Cargando...</p>
              ) : (
                <div className="space-y-2">
                  {paymentFolioSeriesList.map((s: any) => (
                    <div key={s.id} className="flex items-center justify-between border rounded-lg px-3 py-2">
                      <div>
                        <span className="font-mono font-semibold">{s.serie}</span>
                        {s.descripcion && <span className="text-sm text-gray-500 ml-2">{s.descripcion}</span>}
                        <span className="text-xs text-gray-400 ml-2">Folio actual: {s.currentFolio}</span>
                        {!s.isActive && <span className="text-xs text-red-500 ml-2">Inactiva</span>}
                      </div>
                      <div className="flex gap-2">
                        <Button size="sm" variant="outline" onClick={() => { setEditingPaymentSeries(s); setPaymentSeriesForm({ serie: s.serie, descripcion: s.descripcion || '' }); }}>
                          Editar
                        </Button>
                        {s.isActive && (
                          <Button size="sm" variant="outline" className="text-red-600" onClick={() => deactivatePaymentSeriesMutation.mutate(s.id)}>
                            Desactivar
                          </Button>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              )}

              <div className="border-t pt-4 space-y-3">
                <h3 className="font-medium text-sm">{editingPaymentSeries ? `Editar serie "${editingPaymentSeries.serie}"` : 'Agregar nueva serie'}</h3>
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <Label className="text-xs">Serie *</Label>
                    <Input
                      value={paymentSeriesForm.serie}
                      onChange={(e) => setPaymentSeriesForm({ ...paymentSeriesForm, serie: e.target.value.toUpperCase() })}
                      placeholder="A"
                      disabled={!!editingPaymentSeries}
                      maxLength={10}
                    />
                  </div>
                  <div>
                    <Label className="text-xs">Descripción</Label>
                    <Input
                      value={paymentSeriesForm.descripcion}
                      onChange={(e) => setPaymentSeriesForm({ ...paymentSeriesForm, descripcion: e.target.value })}
                      placeholder="Efectivo general"
                    />
                  </div>
                </div>
                <div className="flex justify-end gap-2">
                  {editingPaymentSeries && <Button variant="outline" size="sm" onClick={resetPaymentSeriesForm}>Cancelar</Button>}
                  <Button size="sm" onClick={() => savePaymentSeriesMutation.mutate()} disabled={!paymentSeriesForm.serie || savePaymentSeriesMutation.isPending}>
                    {editingPaymentSeries ? 'Guardar cambios' : 'Agregar serie'}
                  </Button>
                </div>
              </div>

              <div className="border-t pt-4 space-y-2">
                <h3 className="font-medium text-sm">Serie default por Forma de Pago</h3>
                {PAYMENT_METHODS.map((pm) => {
                  const current = paymentDefaults.find((d) => d.paymentMethod === pm);
                  return (
                    <div key={pm} className="flex items-center justify-between text-sm">
                      <span>{PAYMENT_METHOD_LABELS[pm]}</span>
                      <select
                        className="border rounded px-2 py-1 text-sm"
                        value={current?.seriesId ?? ''}
                        onChange={(e) => e.target.value && setDefaultMutation.mutate({ paymentMethod: pm, seriesId: Number(e.target.value) })}
                      >
                        <option value="">Sin serie default</option>
                        {paymentFolioSeriesList.filter((s: any) => s.isActive).map((s: any) => (
                          <option key={s.id} value={s.id}>{s.serie}</option>
                        ))}
                      </select>
                    </div>
                  );
                })}
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Hub Config Modal */}
      {hubProfileId && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg shadow-xl w-full max-w-lg max-h-[90vh] overflow-y-auto">
            <div className="flex justify-between items-center px-6 py-4 border-b sticky top-0 bg-white z-10">
              <h2 className="text-xl font-semibold text-gray-900 flex items-center gap-2">
                <Link2 className="h-5 w-5" />
                Hub QFacturaHub — {profiles.find(p => p.id === hubProfileId)?.rfc}
              </h2>
              <button onClick={() => { setHubProfileId(null); setTestConnResult(null); }} className="text-gray-400 hover:text-gray-600">
                <X className="h-5 w-5" />
              </button>
            </div>

            <div className="p-6 space-y-4">
              <div className="space-y-2">
                <Label htmlFor="hubUrl">Hub URL</Label>
                <Input
                  id="hubUrl"
                  value={hubForm.hubUrl}
                  onChange={(e) => setHubForm({ ...hubForm, hubUrl: e.target.value })}
                  placeholder="https://facturacion.qsoftwaresolutions.com"
                />
                <p className="text-xs text-muted-foreground">Dejar vacío para usar la URL por defecto</p>
              </div>

              <div className="space-y-2">
                <Label htmlFor="clientId">Client ID *</Label>
                <Input
                  id="clientId"
                  value={hubForm.clientId}
                  onChange={(e) => setHubForm({ ...hubForm, clientId: e.target.value })}
                  placeholder="client_xxxxxxxx"
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="clientSecret">Client Secret *</Label>
                {!hubForm.changeSecret ? (
                  <div className="flex items-center gap-2">
                    <Input value="••••••••••••" disabled className="flex-1" />
                    <Button variant="outline" size="sm" onClick={() => setHubForm({ ...hubForm, changeSecret: true })}>
                      Cambiar
                    </Button>
                  </div>
                ) : (
                  <Input
                    id="clientSecret"
                    type="password"
                    value={hubForm.clientSecret}
                    onChange={(e) => setHubForm({ ...hubForm, clientSecret: e.target.value })}
                    placeholder="Ingrese el client secret"
                  />
                )}
              </div>

              <div className="space-y-2">
                <Label htmlFor="razonSocialId">Razón Social ID *</Label>
                {testConnResult?.razones && testConnResult.razones.length > 0 ? (
                  <Select value={hubForm.razonSocialId} onValueChange={(v) => {
                    const rs = testConnResult.razones!.find(r => r.id === v);
                    setHubForm({ ...hubForm, razonSocialId: v, razonSocialRfc: rs?.rfc || '', razonSocialNombre: rs?.nombre || '' });
                  }}>
                    <SelectTrigger>
                      <SelectValue placeholder="Seleccione una razón social" />
                    </SelectTrigger>
                    <SelectContent>
                      {testConnResult.razones.map(r => (
                        <SelectItem key={r.id} value={r.id}>
                          {r.rfc} — {r.nombre}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                ) : (
                  <Input
                    id="razonSocialId"
                    value={hubForm.razonSocialId}
                    onChange={(e) => setHubForm({ ...hubForm, razonSocialId: e.target.value })}
                    placeholder="UUID de la razón social en el Hub"
                  />
                )}
                <p className="text-xs text-muted-foreground">Guarde primero y luego use "Probar Conexión" para ver las opciones disponibles</p>
              </div>

              {/* Test connection */}
              <div className="pt-2 border-t space-y-3">
                <Button
                  variant="outline"
                  onClick={handleTestConnection}
                  disabled={testConnLoading}
                  className="w-full"
                >
                  {testConnLoading ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <RefreshCw className="mr-2 h-4 w-4" />}
                  Probar Conexión
                </Button>

                {testConnResult && (
                  <div className={`rounded-lg p-3 text-sm ${testConnResult.error ? 'bg-red-50 border border-red-200' : 'bg-green-50 border border-green-200'}`}>
                    {testConnResult.error ? (
                      <div className="flex items-center gap-2 text-red-700">
                        <AlertCircle className="h-4 w-4 shrink-0" />
                        <span>{testConnResult.error}</span>
                      </div>
                    ) : (
                      <div className="space-y-1">
                        <div className="flex items-center gap-2 text-green-700 font-medium">
                          <CheckCircle2 className="h-4 w-4" />
                          Conexión exitosa
                        </div>
                        {testConnResult.saldo && (
                          <p className="text-green-700 text-xs">
                            Saldo: {testConnResult.saldo.isUnlimited ? 'Ilimitado' : `${testConnResult.saldo.stampsBalance} timbres`}
                          </p>
                        )}
                        {testConnResult.razones && (
                          <p className="text-green-700 text-xs">
                            {testConnResult.razones.length} razón(es) social(es) disponibles — selecciona una arriba
                          </p>
                        )}
                      </div>
                    )}
                  </div>
                )}
              </div>

              <div className="flex justify-end space-x-3 pt-2 border-t">
                <Button variant="outline" onClick={() => { setHubProfileId(null); setTestConnResult(null); }}>
                  Cancelar
                </Button>
                <Button onClick={handleSaveHubConfig} disabled={saveHubConfigMutation.isPending}>
                  {saveHubConfigMutation.isPending && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
                  Guardar Configuración Hub
                </Button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* SAT Catalogs */}
      <Card>
        <CardHeader>
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2">
              <Database className="h-5 w-5" />
              <div>
                <CardTitle>Catálogos SAT</CardTitle>
                <CardDescription>Claves de productos/servicios y unidades de medida para CFDI 4.0</CardDescription>
              </div>
            </div>
            <Button variant="outline" size="sm" onClick={handleSatSeed} disabled={satSeedLoading}>
              {satSeedLoading ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <FileCheck className="mr-2 h-4 w-4" />}
              Cargar catálogos base
            </Button>
          </div>
        </CardHeader>
        <CardContent>
          <p className="text-sm text-muted-foreground">
            Carga el catálogo base con claves educativas del SAT (servicios escolares, inscripción, tutoría, etc.) y unidades de medida estándar.
            Para gestión completa de catálogos, visita{' '}
            <a href="/app/cfdi/sat-catalogos" className="text-primary underline underline-offset-2">Catálogos SAT</a>.
          </p>
        </CardContent>
      </Card>
    </div>
  );
}
